// @flow
import React from 'react';
import { get, filter, toNumber } from 'lodash';
import { connect } from 'react-redux';
import { router } from '@router';
import { isSecretary, shortenName, isPresent, checkIfIsMobile } from '@helpers/helpers';
import { actions } from '@context/modals';
import {
  getPatient, setPatientFinancialLoading, getPatientFinancial, patientActions, printPatientFinancialReport, selectIsFetchingPatient, selectCanRetryPatient,
  selectFinancialRequest,
} from '@context/patients';
import { serviceActions } from '@context/services';
import { paymentActions } from '@context/payments';
import { receiptActions } from '@context/receipts';
import type { Dispatch, DoctorsState, Patient, ServicesState, PaymentsState, ReceiptsState, PaymentInput, ServiceInput, PlanInclude, UiState } from '@types';
import { PatientFinancial as PatientFinancialView } from '@views';
import { mapPlanIncludes } from '@helpers/connect';
import { withRouter } from 'react-router-dom';
import { push as pushPath } from 'connected-react-router';

type Props = {
  dispatch: Function,
  patient: Patient,
  patientId: number,
  services: ServicesState,
  payments: PaymentsState,
  receipts: ReceiptsState,
  isFetchingPatient: boolean,
  canRetryPatient: boolean,
  paymentLoadingIds: Object,
  serviceLoadingIds: Object,
  isServicesLoading: boolean,
  isPaymentsLoading: boolean,
  isReceiptsLoading: boolean,
  showDoctorSelector: boolean,
  doctors: DoctorsState,
  isBalanceLoading: boolean,
  userId: number,
  planInclude: PlanInclude,
  path: string,
  ui: UiState,
  singleDoctor: boolean,
  isPatientPrinting: boolean,
  balanceRequest: ?string,
};

const clearPath = (path: string) => String(path).replace('/paciente/:id/financeiro', '').replace('/', '').trim();

class PatientFinancial extends React.Component<Props> {
  componentWillMount() {
    if (!this.props.planInclude.financial) {
      return;
    }

    if (isPresent(get(this.props.patient, 'deletedAt'))) {
      this.props.dispatch(router.pushPath('patientDeleted', { id: this.props.patientId }));
    }

    const {
      patient, patientId, dispatch,
    } = this.props;

    // Only a fetch of this patient counts, not the patient list (which shares isLoading). The
    // global getFullPatientError no longer blocks it: one failed patient blocked them all.
    if (!patient || !patient.full) {
      if (this.props.isFetchingPatient) {
        this.waitingForOtherFetch = true;
      } else {
        dispatch(getPatient(patientId));
      }
    }
    if (patient && !checkIfIsMobile(this.props.ui)) {
      if (!this.props.isServicesLoading) {
        this.handleGetServices(patientId);
      }
      if (!this.props.isPaymentsLoading) {
        this.handleGetPayments(patientId);
      }
      if (!this.props.isReceiptsLoading) {
        this.handleGetReceipts(patientId);
      }
      dispatch(setPatientFinancialLoading(patientId, false));
    }
    this.requestBalance(patientId);
  }

  componentWillReceiveProps(nextProps) {
    if (!nextProps.planInclude.financial) {
      return;
    }

    if (isPresent(get(nextProps.patient, 'deletedAt'))) {
      this.props.dispatch(router.pushPath('patientDeleted', { id: nextProps.patientId }));
    }

    const { patient, patientId, dispatch } = this.props;

    // The fetch this page waited for ended without the full patient (it failed): fetch once itself,
    // unless it was a 4xx (a 404 is a patient of another clinic: getPatient left for the patient list)
    if (this.waitingForOtherFetch && this.props.isFetchingPatient && !nextProps.isFetchingPatient) {
      this.waitingForOtherFetch = false;
      if (!get(nextProps.patient, 'full', false) && nextProps.canRetryPatient) dispatch(getPatient(nextProps.patientId));
    }

    // if (!nextProps.getFullPatientError && (nextProps.patient && !nextProps.patient.full && !this.props.isPatientLoading)) {
    //   dispatch(getPatient(patientId));
    // }

    // This patient arrived (not another patient: that is the block below)
    if (!patient && nextProps.patient && patientId === nextProps.patientId && !checkIfIsMobile(nextProps.ui)) {
      this.handleGetServices(patientId);
      this.handleGetPayments(patientId);
      this.handleGetReceipts(patientId);
      dispatch(setPatientFinancialLoading(patientId, false));
    }
    // Another patient on the same route (the page stays mounted): load that patient's balance and lists, not the
    // previous patient's again
    if (this.props.patientId !== nextProps.patientId) {
      if (!get(nextProps.patient, 'full', false) && !nextProps.isFetchingPatient) dispatch(getPatient(nextProps.patientId));
      this.requestBalance(nextProps.patientId);
      this.handleGetServices(nextProps.patientId);
      this.handleGetPayments(nextProps.patientId);
      this.handleGetReceipts(nextProps.patientId);
    }
  }

  render() {
    const {
      patient, paymentLoadingIds, serviceLoadingIds, doctors, showDoctorSelector,
      services, payments, receipts, isPaymentsLoading, isServicesLoading, isBalanceLoading,
      planInclude, path, singleDoctor, isPatientPrinting,
    } = this.props;

    const servicesData = this.patientItems('services', services);
    const paymentsData = this.patientItems('payments', payments);
    const receiptsData = this.patientItems('receipts', receipts);

    return (
      <PatientFinancialView
        isLoading={!patient}
        patient={patient}
        patientId={this.props.patientId}
        services={{ ...services, data: servicesData }}
        payments={{ ...payments, data: paymentsData }}
        receipts={{ ...receipts, data: receiptsData }}
        onNewServicesClick={this.handleNewServicesClick}
        onNewPaymentClick={this.handleNewPaymentClick}
        onShowReceiptClick={this.handleShowReceiptClick}
        onNewReceiptClick={this.handleNewReceiptClick}
        onOpenEditService={this.handleOpenEditService}
        onOpenEditPayment={this.handleOpenEditPayment}
        onUpdatePayment={this.handleOnUpdatePayment}
        onUpdateService={this.handleOnUpdateService}
        paymentLoadingIds={paymentLoadingIds}
        serviceLoadingIds={serviceLoadingIds}
        isPaymentsLoading={isPaymentsLoading}
        isServicesLoading={isServicesLoading}
        isFeatureEnabled={planInclude.financial}
        doctors={doctors}
        isBalanceLoading={isBalanceLoading}
        showDoctorSelector={showDoctorSelector}
        onRequestBalance={this.handleBalanceRequest}
        balanceRequest={this.props.balanceRequest}
        onRetry={this.handleRetry}
        path={clearPath(path)}
        onRefresh={this.handleRefresh}
        onNavigate={this.handleNavigate}
        onDeletePatientProfileImage={this.handleDeletePatientProfileImage}
        onSelectPatientProfileImage={this.handleSelectPatientProfileImage}
        singleDoctor={singleDoctor}
        onPrintReport={this.handlePrintReport}
        isPatientPrinting={isPatientPrinting}
      />
    );
  }

  handlePrintReport = () => {
    const patientId = get(this.props, ['patient', 'id']);
    this.props.dispatch(printPatientFinancialReport(patientId));
  };

  handleSelectPatientProfileImage = (file, fileType) => {
    const patientId = get(this.props, ['patient', 'id']);
    this.props.dispatch(patientActions.uploadPatientProfileImage(patientId, file, fileType));
  };

  handleDeletePatientProfileImage = () => {
    const patientId = get(this.props, ['patient', 'id']);
    const imageId = get(this.props, ['patient', 'profileImage', 'id']);
    this.props.dispatch(patientActions.deletePatientProfileImage(patientId, imageId));
  };

  handleNavigate = (path: string) => {
    this.props.dispatch(pushPath(path));
  };

  requestBalance = (patientId: number | string) => {
    this.props.dispatch(getPatientFinancial(patientId));
  };

  // The view's onRequestBalance (a setState callback: no arguments)
  handleBalanceRequest = () => this.requestBalance(this.props.patientId);

  // Only this patient's lists (the store holds the last patient's, or a report's, until they are fetched again).
  // The same arrays while nothing changed, so the views' memos hold.
  patientItems = (key: string, list: Object) => {
    const id = toNumber(this.props.patientId);
    const data = get(list, 'data');
    const current = get(list, 'currentPatientId');
    this.itemsCache = this.itemsCache || {};
    const cached = this.itemsCache[key];
    if (cached && cached.data === data && cached.id === id && cached.current === current) return cached.items;
    const items = current === id ? filter(data || {}, (item) => toNumber(item.patientId) === id) : [];
    this.itemsCache[key] = { data, id, current, items };
    return items;
  };

  // The overview's "Tentar de novo": only what failed (a list, or the balance)
  handleRetry = () => {
    const { patientId, balanceRequest } = this.props;
    if (get(this.props.services, 'loadFailed')) this.handleGetServices(patientId);
    if (get(this.props.payments, 'loadFailed')) this.handleGetPayments(patientId);
    if (get(this.props.receipts, 'loadFailed')) this.handleGetReceipts(patientId);
    if (balanceRequest === 'failed' || get(this.props.patient, 'financialBalance', null) === null) this.requestBalance(patientId);
  };

  // A list the overview already has (or is loading) for this patient: its page opens with it, no second request
  isListOfThisPatient = (list: Object) => get(list, 'currentPatientId') === toNumber(this.props.patientId) && !get(list, 'loadFailed');

  handleNewServicesClick = (doctorId: number) => {
    this.props.dispatch(actions.openModal('ServicesNewListModal', {
      patientId: this.getPatientId(),
      doctorId,
      doctorName: this.getDoctorName(doctorId),
    }));
  };

  // The modal saves to the patient it was opened for, wherever the page goes meanwhile (see modalPatientId)
  handleOpenEditService = (id: number) => {
    this.props.dispatch(actions.openModal('ServiceFormModal', { id, patientId: this.getPatientId() }));
  };

  // The route's id until the patient has loaded: a "Novo …" tapped right after opening the page crashed on patient.id
  getPatientId = () => toNumber(get(this.props.patient, 'id', this.props.patientId));

  handleNewPaymentClick = (doctorId: number) => {
    this.props.dispatch(actions.openModal('PaymentNewFormModal', {
      patientId: this.getPatientId(),
      doctorId,
      doctorName: this.getDoctorName(doctorId),
    }));
  };

  handleOpenEditPayment = (data) => {
    const { paymentId, patientId } = data;
    this.props.dispatch(actions.openModal('PaymentEditFormModal', { paymentId, patientId }));
  };

  handleShowReceiptClick = (receiptId: number) => {
    this.props.dispatch(actions.openModal('ReceiptModal', { patientId: this.getPatientId(), receiptId }));
  };

  handleNewReceiptClick = (doctorId: number, price: number, date: string) => {
    const { dispatch, userId } = this.props;
    dispatch(actions.openModal('ReceiptNewFormModal', {
      doctorId: !doctorId ? userId : doctorId,
      patientId: this.getPatientId(),
      price,
      date,
    }));
  };

  handleGetServices = (patientId) => {
    this.props.dispatch(serviceActions.getPatientServices(patientId));
  };

  handleGetPayments = (patientId) => {
    this.props.dispatch(paymentActions.getPatientPayments(patientId));
  };

  handleGetReceipts = (patientId) => {
    // Already on its way for this patient (another patient's or a report's request doesn't count)
    if (this.props.isReceiptsLoading && get(this.props.receipts, 'currentPatientId') === toNumber(patientId)) return;
    this.props.dispatch(receiptActions.getPatientReceipts(patientId));
  };


  handleRefresh= (action: string) => {
    // A clinic without the financial feature sees the upgrade notice: nothing to load
    if (!this.props.planInclude.financial) return;
    switch (action) {
      // A list page opened from the phone's overview: it already has the list
      case 'services': if (!this.isListOfThisPatient(this.props.services)) this.handleGetServices(this.props.patientId); break;
      case 'payments': if (!this.isListOfThisPatient(this.props.payments)) this.handleGetPayments(this.props.patientId); break;
      case 'receipts': if (!this.isListOfThisPatient(this.props.receipts)) this.handleGetReceipts(this.props.patientId); break;
      // The page itself: the desktop lists all three, and the phone's overview counts them and shows the latest
      // services and payments (one request each, the same three the desktop page makes)
      default:
        this.handleGetServices(this.props.patientId);
        this.handleGetPayments(this.props.patientId);
        this.handleGetReceipts(this.props.patientId);
        break;
    }
  };

  handleOnUpdatePayment = (id: number, data: PaymentInput) => {
    this.props.dispatch(paymentActions.updatePayment(this.getPatientId(), id, data));
  };

  handleOnUpdateService = (id: number, data: ServiceInput) => {
    this.props.dispatch(serviceActions.updateService(this.getPatientId(), id, data));
  };

  getDoctorName = (doctorId: number) => shortenName(get(this.props, ['doctors', 'data', String(doctorId), 'nameWithTitle'], ''));
}


const mapStateToProps = (state, ownProps: Object) => ({
  patient: get(state.context, ['patients', 'data', ownProps.match.params.id]),
  patientId: get(ownProps, ['match', 'params', 'id']),
  isFetchingPatient: selectIsFetchingPatient(state, ownProps.match.params.id),
  canRetryPatient: selectCanRetryPatient(state, ownProps.match.params.id),
  isPatientPrinting: get(state, ['context', 'patients', 'isPrinting'], false),
  isReceiptsLoading: get(state, ['context', 'receipts', 'isLoading'], false),
  isBalanceLoading: get(state, ['context', 'patients', 'isFinancialLoading'], false),
  balanceRequest: selectFinancialRequest(state, ownProps.match.params.id),
  isServicesLoading: get(state, ['context', 'services', 'isSaving'], false) || get(state, ['context', 'services', 'isLoading'], false),
  isPaymentsLoading: get(state, ['context', 'payments', 'isSaving'], false) || get(state, ['context', 'services', 'isLoading'], false),
  services: state.context.services,
  payments: state.context.payments,
  receipts: state.context.receipts,
  doctors: get(state, ['context', 'doctors'], {}),
  paymentLoadingIds: get(state, ['context', 'payments', 'updatingIds'], {}),
  serviceLoadingIds: get(state, ['context', 'services', 'updatingIds'], {}),
  showDoctorSelector: isSecretary(state),
  userId: get(state, ['context', 'user', 'advancedData', 'doctorId']),
  planInclude: mapPlanIncludes(state),
  path: get(ownProps, ['match', 'path'], ''),
  singleDoctor: get(state, ['context', 'clinics', 'activeClinic', 'singleDoctor'], false),
  ui: state.ui,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({ dispatch });

export default connect<any, any, any, any, any, any>(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(PatientFinancial));
